// ============================================================================
// client/papier.ts — LA MATIÈRE « PAPIER DÉCOUPÉ » ET LE PANTIN ARTICULÉ
// ----------------------------------------------------------------------------
// Direction artistique choisie par Claude-Alain le 28/07/2026 (variante 4 des
// quatre proposées dans /pages/refonte-d.html). Ce module est le PORTAGE EXACT
// de la maquette validée dans /pages/refonte-d-perso.html : le code de dessin y
// a été mis au point à l'image, il est repris tel quel plutôt que réécrit, pour
// que le jeu affiche ce qui a été validé et rien d'autre.
//
// Les règles que ce module fait respecter, et qu'il ne faut pas défaire :
//   · UN SEUL POINT DE VUE — trois-quarts orienté, deux plans de profondeur.
//     Le plan LOIN est dessiné en premier, assombri et plus étroit ; le plan
//     PROCHE en dernier, pleine teinte. C'est l'assombrissement qui fait la
//     profondeur, jamais un dessin supplémentaire.
//   · ÉPAULES ET HANCHES DISSYMÉTRIQUES — l'avant est avancé, l'arrière reculé.
//     C'est cela, et cela seul, qui dit de quel côté le corps regarde.
//   · LE COUDE NE PLIE QUE DANS UN SENS — l'angle de coude est toujours NÉGATIF,
//     sinon l'avant-bras part en arrière et le bras se casse à l'envers.
//   · LES POINTS DE CONTACT SE CALCULENT — la hauteur du pied et celle des mains
//     se déduisent des angles ; on ne les règle jamais à l'œil.
//   · L'OMBRE PORTÉE EST RÉSERVÉE À CE QUI SE JOUE. Le décor est collé à plat.
//
// Module PUR : aucune dépendance au jeu, aucun état, rien qui entre dans le
// hash de simulation. Il ne suppose qu'un CanvasRenderingContext2D.
// ============================================================================

type Ctx = CanvasRenderingContext2D
interface Boite { x: number; y: number; w: number; h: number }
export interface Tenue { id?: string; nom?: string; torse: string; jambe: string; motif: string; accent: string }

export const PAP = '#f2e4cc', ENCRE = '#3a2c1e', OMBRE_TON = '#2e2214'
export const rgba = (hex: string, a: number): string => { const n = parseInt(hex.slice(1), 16)
  return `rgba(${n >> 16 & 255},${n >> 8 & 255},${n & 255},${a})` }
export function bruit(x: number, y: number): number { let h = ((x | 0) * 73856093) ^ ((y | 0) * 19349663); h = (h ^ (h >>> 13)) >>> 0; return (h % 1000) / 1000 }

/* Un PAPIER : aplat + motif imprimé + grain de fibre. Jamais un aplat nu. */
/** Niveau de détail courant, réglé par le pantin selon sa taille (voir DETAIL).
 *  1 = pleine matière (planches, vestiaire) · 0 = silhouette nette (taille de jeu). */
/* ⚠️ DETAIL est un état GLOBAL du module, et c'est un piège : le dernier qui a
   peint impose son réglage au suivant. Le héros (`pantin`) l'éteint à chaque
   image parce qu'il est dessiné à 44 px. Tout appelant qui peint dans un canvas
   à lui DOIT donc poser sa propre valeur en entrant, et non supposer celle
   qu'il trouve — voir `paintTerrainStatic`, qui a mangé exactement cette faute. */
let DETAIL = 1
export function setDetail(v: number): void { DETAIL = Math.max(0, Math.min(1, v)) }
/** Le réglage courant, pour le rendre après l'avoir changé localement. */
export function getDetail(): number { return DETAIL }

export function papier(g: Ctx, x: number, y: number, w: number, h: number, col: string, motif: string, ton?: string): void {
  g.save(); g.beginPath(); g.rect(x, y, w, h); g.clip()
  g.fillStyle = col; g.fillRect(x, y, w, h)
  const T = ton || '#000000'
  if (motif === 'rayures') { g.fillStyle = rgba(T, .2)
    for (let i = -h; i < w; i += 6) { g.save(); g.translate(x + i, y); g.rotate(.5); g.fillRect(0, -h, 2.4, h * 3); g.restore() } }
  else if (motif === 'pois') { g.fillStyle = rgba(T, .2)
    for (let yy = 2; yy < h; yy += 7) for (let xx = ((yy / 7) | 0) % 2 * 4 + 2; xx < w; xx += 8) { g.beginPath(); g.arc(x + xx, y + yy, 1.7, 0, 7); g.fill() } }
  else if (motif === 'chevrons') { g.strokeStyle = rgba(T, .26); g.lineWidth = 1.5
    for (let yy = -w; yy < h + w; yy += 8) { g.beginPath(); g.moveTo(x, y + yy); g.lineTo(x + w / 2, y + yy + w / 2); g.lineTo(x + w, y + yy); g.stroke() } }
  else if (motif === 'quadrille') { g.strokeStyle = rgba(T, .22); g.lineWidth = 1
    for (let xx = 4; xx < w; xx += 7) { g.beginPath(); g.moveTo(x + xx, y); g.lineTo(x + xx, y + h); g.stroke() }
    for (let yy = 4; yy < h; yy += 7) { g.beginPath(); g.moveTo(x, y + yy); g.lineTo(x + w, y + yy); g.stroke() } }
  else if (motif === 'marbre') { g.strokeStyle = rgba(T, .22); g.lineWidth = 1.8
    for (let k = 0; k < 5; k++) { g.beginPath(); g.moveTo(x, y + h * (k + .4) / 5)
      for (let xx = 0; xx <= w; xx += 5) g.lineTo(x + xx, y + h * (k + .4) / 5 + Math.sin(xx / 5 + k) * 2.6); g.stroke() } }
  else if (motif === 'guilloche') { g.strokeStyle = rgba('#ffffff', .28); g.lineWidth = 1
    for (let k = 0; k < 8; k++) { g.beginPath(); g.arc(x + w / 2, y + h / 2, 3 + k * 3, 0, 7); g.stroke() } }
  else if (motif === 'briques') { g.strokeStyle = rgba(T, .3); g.lineWidth = 1.4
    for (let r = 1; r < 3; r++) { g.beginPath(); g.moveTo(x, y + r * h / 3); g.lineTo(x + w, y + r * h / 3); g.stroke() }
    for (let r = 0; r < 3; r++) { const dx = r % 2 ? w / 2 : 0
      g.beginPath(); g.moveTo(x + dx, y + r * h / 3); g.lineTo(x + dx, y + (r + 1) * h / 3); g.stroke() } }
  else if (motif === 'plis') { g.fillStyle = rgba('#ffffff', .2)
    g.fillRect(x + 3, y + h * .35, w - 8, 2); g.fillRect(x + 7, y + h * .66, w - 16, 1.6) }
  /* Le motif de l'EAU, et le seul calé sur la coordonnée du MONDE. Tous les
     autres repartent de l'origine de leur pièce, ce qui est juste pour une
     brique — chacune est une pièce distincte. Un bassin, lui, est UNE SEULE
     nappe : si ses plis repartaient à chaque case, on lirait la grille avant
     l'eau. Le pas (23) est premier avec la case (48) : deux rangées voisines
     ne retombent jamais sur le même décalage. */
  else if (motif === 'eau') { g.fillStyle = rgba('#ffffff', .13)
    const PAS = 23, PER = 400            // période horizontale : ~8 cases
    for (let yy = Math.floor(y / PAS) * PAS; yy < y + h + PAS; yy += PAS) {
      const k = Math.floor(yy / PAS)
      const base = Math.floor(x / PER) * PER + ((k * 137) % PER) - PER
      for (let xx = base; xx < x + w + PER; xx += PER) {
        g.fillRect(xx, yy, 130, 1.8); g.fillRect(xx + 205, yy, 88, 1.6)
      }
    } }
  // le grain de fibre, commun à TOUS les papiers : c'est lui qui unifie la planche.
  // À petite taille il ne se lit plus comme de la matière mais comme du bruit :
  // il s'éteint sous le seuil de détail.
  const grains = DETAIL < .5 ? 0 : (w * h / 26) * DETAIL
  for (let i = 0; i < grains; i++) {
    const nx = x + ((bruit(x + i * 5, y + i * 2) * w) | 0), ny = y + ((bruit(y + i * 3, x + i) * h) | 0)
    g.fillStyle = bruit(ny, nx) > .5 ? rgba('#000000', .1) : rgba('#ffffff', .13); g.fillRect(nx, ny, 2, 2)
  }
  g.restore()
}

/* Une DÉCOUPE de forme libre : ombre portée, aplat, motif, grain.
   ⚠️ `boite` est OBLIGATOIRE quand le contexte est transformé (translate/rotate) :
   le papier se peint dans l'espace COURANT, pas dans celui du canvas. Sans elle,
   le remplissage rate la zone découpée et la pièce ressort grise. */
export function decoupe(g: Ctx, path: () => void, col: string, motif: string, boite?: Boite | null, dx = 3, dy = 4, aOmbre = .28): void {
  if (aOmbre > 0) { g.save(); g.translate(dx, dy); g.fillStyle = rgba(OMBRE_TON, aOmbre); path(); g.fill(); g.restore() }
  const b = boite || { x: 0, y: 0, w: g.canvas.width, h: g.canvas.height }
  g.save(); path(); g.clip()
  papier(g, b.x, b.y, b.w, b.h, col, motif, OMBRE_TON)
  g.restore()
}
/* Un rectangle de papier : pas de clip du tout, donc jamais de piège d'espace. */
export function rect(g: Ctx, x: number, y: number, w: number, h: number, col: string, motif: string, ombre = true): void {
  if (ombre) { g.fillStyle = rgba(OMBRE_TON, .28); g.fillRect(x + 3, y + 4, w, h) }
  papier(g, x, y, w, h, col, motif, OMBRE_TON)
}

/* La FRANGE : le bord d'une masse n'est jamais droit, il sort des ciseaux. */
export function frange(g: Ctx, x: number, y: number, w: number, col: string, sens = -1): void {
  g.fillStyle = col
  for (let i = 0; i < w; i += 3) {
    const d = 1 + ((bruit(x + i, y) * 3) | 0)
    g.fillRect(x + i, sens < 0 ? y - d : y, 3, d)
  }
}
/* Une frange DENTELÉE, réservée à ce qui tue : elle se lit avant la couleur. */
export function dents(g: Ctx, x: number, y: number, w: number, col: string, h = 6): void {
  g.fillStyle = col
  for (let i = 0; i < w - 1; i += 7) {
    g.beginPath(); g.moveTo(x + i, y); g.lineTo(x + i + 3.5, y - h); g.lineTo(x + i + 7, y); g.closePath(); g.fill()
  }
}

/** Une pose : rien que des nombres, ce qui permet de les INTERPOLER. */
export interface Pose {
  nom?: string
  bassinY?: number; bassinRot?: number
  buste?: number[]; tete?: number[]
  cuAV: number; gnAV: number; cuAR: number; gnAR: number
  epAV: number; cdAV: number; epAR: number; cdAR: number
  foulard?: number; ecrase?: number; etire?: number; solW?: number
  face?: boolean; air?: boolean; pend?: boolean; glisse?: boolean
  outil?: string; impact?: boolean
  [k: string]: unknown
}
export interface OptsPantin {
  ecart?: number; bob?: number; face?: number; cligne?: boolean; tick?: number
}

export const TENUES: Tenue[] = [
  { id: 'origine', nom: 'Baroudeur', torse: '#2f6fc8', jambe: '#2a4a7a', motif: 'plis', accent: '#ffcf1f' },
  { id: 'lave', nom: 'Lave', torse: '#61200f', jambe: '#33130c', motif: 'marbre', accent: '#ffb347' },
]
const PEAU = '#f5cfa0', BOTTE = '#4a3320', GANT = '#8a5a30'
/* Le plan LOIN est assombri : c'est ce qui fait la profondeur, pas le dessin. */
export function sombre(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16)
  const c = [n >> 16 & 255, n >> 8 & 255, n & 255].map((v) => Math.max(0, Math.round(v * f)))
  return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('')
}
const LOIN = .68                                   // facteur d'assombrissement du plan arrière

/* Une pose = un jeu d'angles, plus la hauteur et la torsion du bassin.
   AV = membre du plan proche · AR = membre du plan lointain.
   Angles en radians, 0 = le membre pend vers le bas. Cotes en 44e de la hauteur.

   LE CYCLE DE COURSE est construit comme un vrai cycle, pas comme deux images
   alternées : contact, amorti (bassin au plus bas), poussée et suspension
   (bassin au plus haut), puis la même chose de l'autre jambe. Six clés. Le bras
   contrebalance TOUJOURS la jambe opposée, et la tête contre-tourne le buste
   pour que le regard reste à l'horizontale. */
export const POSES: Record<string, Pose> = {
  idle: { nom: 'repos', bassinY: 0, bassinRot: 0, buste: [0, 0, .04], tete: [0, 0, -.03],
    cuAV: .1, gnAV: .06, cuAR: -.12, gnAR: .1, epAV: -.1, cdAV: -.16, epAR: .12, cdAR: -.14, foulard: .12 },
  // ---- course : 6 clés (contact, amorti, suspension) × 2 jambes ------------
  run0: { nom: 'course · contact', bassinY: 0, bassinRot: -.1, buste: [4, 0, .25], tete: [5.5, 0, -.05],
    cuAV: -.72, gnAV: .12, cuAR: .58, gnAR: .78, epAV: .78, cdAV: -.5, epAR: -.62, cdAR: -.55, foulard: .8 },
  run1: { nom: 'course · amorti', bassinY: 2.5, bassinRot: -.04, buste: [4, 2.5, .25], tete: [5.5, 2, -.05], solW: 0.55,
    cuAV: -.22, gnAV: .34, cuAR: .42, gnAR: 1.35, epAV: .42, cdAV: -.55, epAR: -.34, cdAR: -.6, foulard: .6 },
  run2: { nom: 'course · envol', bassinY: -3.5, bassinRot: .04, buste: [4, -3.5, .25], tete: [5.5, -3, -.05], solW: 0,
    cuAV: .5, gnAV: 1.25, cuAR: -.68, gnAR: .55, epAV: -.2, cdAV: -.5, epAR: .28, cdAR: -.5, foulard: 1.05 },
  run3: { nom: 'course · contact 2', bassinY: 0, bassinRot: .1, buste: [4, 0, .25], tete: [5.5, 0, -.05],
    cuAV: .58, gnAV: .78, cuAR: -.72, gnAR: .12, epAV: -.62, cdAV: -.55, epAR: .78, cdAR: -.5, foulard: .8 },
  run4: { nom: 'course · amorti 2', bassinY: 2.5, bassinRot: .04, buste: [4, 2.5, .25], tete: [5.5, 2, -.05], solW: 0.55,
    cuAV: .42, gnAV: 1.35, cuAR: -.22, gnAR: .34, epAV: -.34, cdAV: -.6, epAR: .42, cdAR: -.55, foulard: .6 },
  run5: { nom: 'course · envol 2', bassinY: -3.5, bassinRot: -.04, buste: [4, -3.5, .25], tete: [5.5, -3, -.05], solW: 0,
    cuAV: -.68, gnAV: .55, cuAR: .5, gnAR: 1.25, epAV: .28, cdAV: -.5, epAR: -.2, cdAR: -.5, foulard: 1.05 },
  // ---- échelle : vue de FACE, seule pose où les deux côtés sont symétriques -
  climb0: { nom: 'échelle 1', bassinY: 0, bassinRot: 0, buste: [0, 0, 0], tete: [0, 0, 0], face: true, solW: 0,
    cuAV: .16, gnAV: .14, cuAR: -.4, gnAR: .62, epAV: .3, cdAV: -.22, epAR: -2.5, cdAR: -.28, foulard: .2 },
  climb1: { nom: 'échelle m.', bassinY: -1.5, bassinRot: 0, buste: [0, -1.5, 0], tete: [0, -1.5, 0], face: true, solW: 0,
    cuAV: -.1, gnAV: .34, cuAR: -.1, gnAR: .34, epAV: -1.5, cdAV: -.05, epAR: -1.5, cdAR: -.05, foulard: .3 },
  climb2: { nom: 'échelle 2', bassinY: 0, bassinRot: 0, buste: [0, 0, 0], tete: [0, 0, 0], face: true, solW: 0,
    cuAV: -.4, gnAV: .62, cuAR: .16, gnAR: .14, epAV: -2.5, cdAV: -.28, epAR: .3, cdAR: -.22, foulard: .2 },
  // ---- corde : suspendu, les deux bras au-dessus, le corps qui pendule -----
  rope0: { nom: 'corde 1', bassinY: 2, bassinRot: -.12, buste: [0, 2, -.06], tete: [0, 1, .04], pend: true, solW: 0,
    cuAV: -.35, gnAV: .5, cuAR: .2, gnAR: .3, epAV: -2.45, cdAV: -.2, epAR: 2.55, cdAR: -.2, foulard: .5 },
  rope1: { nom: 'corde m.', bassinY: 3, bassinRot: 0, buste: [0, 3, 0], tete: [0, 2, 0], pend: true, solW: 0,
    cuAV: -.08, gnAV: .38, cuAR: -.08, gnAR: .38, epAV: -2.6, cdAV: -.14, epAR: 2.6, cdAR: -.14, foulard: .35 },
  rope2: { nom: 'corde 2', bassinY: 2, bassinRot: .12, buste: [0, 2, .06], tete: [0, 1, -.04], pend: true, solW: 0,
    cuAV: .2, gnAV: .3, cuAR: -.35, gnAR: .5, epAV: -2.55, cdAV: -.2, epAR: 2.45, cdAR: -.2, foulard: .5 },
  // ---- chute : le corps se met en boule vers l'arrière ---------------------
  fall: { nom: 'chute', bassinY: 1, bassinRot: -.05, buste: [-1, 1, -.08], tete: [-1, 1, .06], air: true, solW: 0,
    cuAV: -.42, gnAV: .68, cuAR: .3, gnAR: .95, epAV: -1.7, cdAV: -.45, epAR: 1.5, cdAR: -.4, foulard: 1.3 },
  fallA: { nom: 'chute m.', bassinY: 2, bassinRot: .05, buste: [-1, 2, -.03], tete: [-1, 2, .02], air: true, solW: 0,
    cuAV: -.28, gnAV: .55, cuAR: .48, gnAR: 1.1, epAV: -1.8, cdAV: -.3, epAR: 1.35, cdAR: -.5, foulard: 1.1 },
  // ---- creusage : le buste se tord vers l'avant, la pioche part en arrière -
  dig: { nom: 'creusage · armé', bassinY: -1, bassinRot: .1, buste: [-1, -1, -.12], tete: [1, -1, .14], outil: 'pioche',
    cuAV: .28, gnAV: .26, cuAR: -.3, gnAR: .3, epAV: 2.35, cdAV: -.75, epAR: .45, cdAR: -.4, foulard: .35 },
  digA: { nom: 'creusage · descente', bassinY: 1, bassinRot: 0, buste: [2, 1, .2], tete: [3, 0, -.06], outil: 'pioche',
    cuAV: .3, gnAV: .3, cuAR: -.32, gnAR: .34, epAV: 1.1, cdAV: -.5, epAR: .5, cdAR: -.42, foulard: .75 },
  pickHit: { nom: 'creusage · impact', bassinY: 3, bassinRot: -.08, buste: [5, 3, .5], tete: [6, 2, -.26], outil: 'pioche', impact: true,
    cuAV: .4, gnAV: .5, cuAR: -.38, gnAR: .52, epAV: -.62, cdAV: -.18, epAR: .55, cdAR: -.45, foulard: 1.05 },
  // ---- accroupi : le bassin descend, les genoux remontent ------------------
  crouch: { nom: 'accroupi', bassinY: 9, bassinRot: 0, buste: [1, 9, .18], tete: [1, 9, -.12], ecrase: .88,
    cuAV: -.7, gnAV: 1.75, cuAR: .55, gnAR: 1.85, epAV: .35, cdAV: -.6, epAR: -.3, cdAR: -.55, foulard: .2 },
  // ---- dash : tout part vers l'arrière, le buste plonge -------------------
  dash0: { nom: 'dash · départ', bassinY: 0, bassinRot: -.12, buste: [5, 0, .46], tete: [7, 1, -.24], air: true, etire: 1.04, solW: 0,
    cuAV: -.85, gnAV: .3, cuAR: .95, gnAR: .28, epAV: 1.05, cdAV: -.35, epAR: -1.15, cdAR: -.45, foulard: 1.5 },
  dashA: { nom: 'dash · pointe', bassinY: -1, bassinRot: 0, buste: [6, -1, .5], tete: [8, 0, -.26], air: true, etire: 1.09, solW: 0,
    cuAV: -.45, gnAV: .45, cuAR: 1.15, gnAR: .4, epAV: 1.2, cdAV: -.3, epAR: -1.3, cdAR: -.4, foulard: 1.75 },
  dash1: { nom: 'dash · fin', bassinY: 0, bassinRot: .12, buste: [6, 0, .48], tete: [8, 1, -.25], air: true, etire: 1.05, solW: 0,
    cuAV: -.2, gnAV: .65, cuAR: 1.25, gnAR: .55, epAV: 1.15, cdAV: -.35, epAR: -1.25, cdAR: -.45, foulard: 1.6 },
  // ---- freinage : le buste part EN ARRIÈRE, la jambe avant bloque ---------
  brake: { nom: 'freinage', bassinY: 2, bassinRot: .14, buste: [-4, 2, -.34], tete: [-3, 1, .22], glisse: true,
    cuAV: -1.05, gnAV: .12, cuAR: .5, gnAR: 1, epAV: -1.05, cdAV: -.3, epAR: .85, cdAR: -.3, foulard: 1.4 },
  // ---- ramassage : le buste plie, la main avant descend -------------------
  pickUp: { nom: 'ramasse', bassinY: 4, bassinRot: -.08, buste: [3, 4, .32], tete: [4, 3, -.16],
    cuAV: -.5, gnAV: 1.15, cuAR: .5, gnAR: 1.1, epAV: -.15, cdAV: -.85, epAR: .3, cdAR: -.65, foulard: .25 },
  // ---- trampoline : compression, envol, retombée --------------------------
  tramp0: { nom: 'tremplin · appui', bassinY: 9, bassinRot: 0, buste: [0, 9, .1], tete: [0, 9, -.06], ecrase: .82,
    cuAV: -1, gnAV: 1.85, cuAR: .95, gnAR: 1.9, epAV: .5, cdAV: -.65, epAR: -.45, cdAR: -.6, foulard: .1 },
  tramp1: { nom: 'tremplin · envol', bassinY: -3, bassinRot: 0, buste: [0, -3, -.04], tete: [0, -3, .02], air: true, etire: 1.2, solW: 0,
    cuAV: -.1, gnAV: .06, cuAR: .1, gnAR: .06, epAV: -2.7, cdAV: -.12, epAR: 2.7, cdAR: -.12, foulard: 1.6 },
  tramp2: { nom: 'tremplin · sommet', bassinY: -1, bassinRot: .06, buste: [0, -1, .06], tete: [0, -1, -.04], air: true, solW: 0,
    cuAV: -.42, gnAV: .55, cuAR: .42, gnAR: .5, epAV: -1.85, cdAV: -.35, epAR: 1.8, cdAR: -.35, foulard: 1 },
}
/* Les 18 poses que le moteur réclame. Les clés supplémentaires (amorti, envol,
   descente) sont des intermédiaires d'animation : elles ne coûtent que des nombres. */
export const ORDRE_POSES: readonly string[] = ['idle', 'run0', 'run3', 'climb0', 'climb2', 'rope0', 'rope2', 'fall',
                     'dig', 'crouch', 'dash0', 'dash1', 'brake', 'pickUp', 'pickHit',
                     'tramp0', 'tramp1', 'tramp2']

/* L'INTERPOLATION : deux poses, un curseur entre 0 et 1, et voilà une image qui
   n'existe dans aucune planche. C'est le vrai gain du pantin sur un sprite : on
   passe de 2 images par cycle à autant qu'on veut, sans un coup de crayon de plus. */
const SCAL = ['bassinY', 'bassinRot', 'solW', 'cuAV', 'gnAV', 'cuAR', 'gnAR',
              'epAV', 'cdAV', 'epAR', 'cdAR', 'foulard', 'ecrase', 'etire']
const VECT = ['buste', 'tete']
const lissage = (t: number): number => t * t * (3 - 2 * t)
export function melange(a: Pose, b: Pose, tBrut: number): Pose {
  const t = lissage(Math.max(0, Math.min(1, tBrut)))
  const o: Pose = { ...a, nom: a.nom, face: a.face, air: a.air, pend: a.pend, glisse: a.glisse,
    outil: a.outil || b.outil, impact: t > .5 ? b.impact : a.impact }
  for (const k of SCAL) { const un = (k === 'ecrase' || k === 'etire' || k === 'solW') ? 1 : 0
    const va = (a[k] as number | undefined) ?? un, vb = (b[k] as number | undefined) ?? un
    o[k] = va + (vb - va) * t }
  for (const k of VECT) { const va = (a[k] as number[]) || [0, 0, 0], vb = (b[k] as number[]) || [0, 0, 0]
    o[k] = [0, 1, 2].map((i) => (va[i] || 0) + ((vb[i] || 0) - (va[i] || 0)) * t) }
  return o
}

/* ══════════════════════════════════════════════════════════════════════════
   Le dessin. Ordre imposé par la profondeur, jamais par commodité :
     jambe LOIN · bras LOIN · bassin · torse · tête · jambe PROCHE · bras PROCHE
   ══════════════════════════════════════════════════════════════════════════ */
/* La hauteur des mains d'une pose, en pixels au-dessus de `base`. Sert à accrocher
   le personnage à une corde PAR SES MAINS, plutôt qu'à régler un décalage à l'œil. */
export function hauteurMains(p: Pose, T: number): number {
  const u = T / 44, BU = (p.buste ?? [0, 0, 0]) as [number, number, number]
  const y = (ep: number, cd: number, av: boolean): number => -31 * u + BU[1] * u + (p.face ? 0 : (av ? .8 : -1.2) * u)
                          + Math.cos(ep) * 9 * u + Math.cos(ep + cd) * 12 * u
  return Math.min(y(p.epAV, p.cdAV, true), y(p.epAR, p.cdAR, false))
}

export function pantin(g: Ctx, cx: number, base: number, T: number, tenue: Tenue, chapeau: string, pose: Pose | string, opts: OptsPantin = {}): void {
  const p = (typeof pose === 'string' ? (POSES[pose] ?? POSES.idle) : pose) as Pose
  const u = T / 44
  const { ecart = 0, bob = 0, face = 1, cligne = false, tick = 0 } = opts
  const BU = (p.buste ?? [0, 0, 0]) as [number, number, number]
  const TE = (p.tete ?? [0, 0, 0]) as [number, number, number]
  const E = (dx: number, dy: number): [number, number] => ecart ? [dx * ecart * 2, dy * ecart * 2] : [0, 0]
  const deFace = !!p.face

  /* Les couleurs du plan LOIN : mêmes papiers, assombris. */
  const cTorseAR = sombre(tenue.torse, LOIN), cJambeAR = sombre(tenue.jambe, LOIN)
  const cBotteAR = sombre(BOTTE, LOIN), cGantAR = sombre(GANT, LOIN)

  /* Sous 70 px de haut, on est à la taille du jeu : la matière fine cesse d'aider
     et commence à brouiller. Au-dessus (planches, vestiaire), pleine matière. */
  setDetail(T >= 96 ? 1 : T <= 56 ? 0 : (T - 56) / 40)

  g.save()
  g.translate(cx, base + bob * u)
  if (face < 0) g.scale(-1, 1)
  g.scale(1, (p.ecrase || 1) * (p.etire || 1))

  /* La hauteur de hanche est FIXE (21u) : cuisse 9 + tibia 7 + botte 5. C'est
     ce qui garantit que le pied touche exactement le sol au repos. Le bassin
     monte et descend par `bassinY`, et le sol suit — jamais l'inverse. */
  const HANCHE = -21 * u + (p.bassinY || 0) * u
  /* Le pied, calculé et non deviné : hanche + cuisse (9) + tibia et botte (12),
     chacun projeté sur la verticale par le cosinus de son angle cumulé. */
  const piedY = (cu: number, gn: number): number => HANCHE + Math.cos(cu) * 9 * u + Math.cos(cu + gn) * 12 * u
  /* et la MAIN, sur le même principe : épaule + bras (9) + avant-bras et gant (12). */
  const mainY = (ep: number, cd: number, av: boolean): number => -31 * u + BU[1] * u + (deFace ? 0 : (av ? .8 : -1.2) * u)
                              + Math.cos(ep) * 9 * u + Math.cos(ep + cd) * 12 * u
  /* Tant que la pose touche le sol, on translate l'ensemble pour que le pied le
     plus bas y arrive exactement. Une pose en l'air (chute, dash, corde) garde
     sa hauteur : c'est le geste qui décide, pas le sol. */
  const solW = p.solW ?? 1
  const calage = solW * -Math.max(piedY(p.cuAV, p.gnAV), piedY(p.cuAR, p.gnAR))
  if (calage) g.translate(0, calage)
  /* Les hanches ne sont PAS symétriques : celle de devant est avancée. La torsion
     du bassin (`bassinRot`) les décale l'une par rapport à l'autre en profondeur. */
  const hancheX = (av: boolean): number => (deFace ? (av ? 5.5 : -5.5) : (av ? 5 : -3.5)) * u + BU[0] * u / 3
                        + (av ? 1 : -1) * (p.bassinRot || 0) * 7 * u

  const jambe = (av: boolean, cu: number, gn: number): void => {
    const l = av ? 1 : .88                                   // le plan loin est plus étroit
    const cj = av ? tenue.jambe : cJambeAR, cb = av ? BOTTE : cBotteAR
    const [gx, gy] = E(av ? 11 : -11, 13)
    g.save(); g.translate(hancheX(av) + gx, HANCHE + gy); g.rotate(cu)
    rect(g, -4.6 * l * u, 0, 9.2 * l * u, 9 * u, cj, tenue.motif)          // cuisse
    g.translate(0, 9 * u); g.rotate(gn)
    rect(g, -4.2 * l * u, 0, 8.4 * l * u, 7 * u, cj, tenue.motif)          // tibia
    g.translate(0, 7 * u)
    rect(g, -5.4 * l * u, 0, (av ? 11.5 : 10) * u, 5 * u, cb, 'plis')      // botte, orientée
    g.fillStyle = rgba('#ffffff', av ? .18 : .1); g.fillRect(-5.4 * l * u, 0, 11 * l * u, 1.3 * u)
    if (av) { g.fillStyle = '#c8a03a'; g.fillRect(-4.2 * u, 1.8 * u, 2 * u, 1.6 * u) }  // boucle
    g.restore()
  }

  /* Les ÉPAULES, le point que la version 1 ratait : elles ne sont pas en face
     l'une de l'autre. L'avant est AVANCÉE et un peu BASSE, l'arrière RECULÉE et
     un peu HAUTE. C'est cette dissymétrie qui dit de quel côté le corps regarde. */
  const epauleX = (av: boolean): number => (deFace ? (av ? 11 : -11) : (av ? 8.5 : -6)) * u + BU[0] * u * .4
  const epauleY = (av: boolean): number => -31 * u + BU[1] * u + (deFace ? 0 : (av ? .8 : -1.2) * u)

  const bras = (av: boolean, ep: number, cd: number): void => {
    const l = av ? 1 : .86
    const ct = av ? tenue.torse : cTorseAR, cg = av ? GANT : cGantAR
    const [gx, gy] = E(av ? 21 : -21, -4)
    g.save(); g.translate(epauleX(av) + gx, epauleY(av) + gy); g.rotate(ep)
    /* la manche : elle part de l'épaule et fait corps avec le torse, au lieu de
       la rondelle plaquée de la version 1 */
    rect(g, -3.4 * l * u, -2.2 * u, 6.8 * l * u, 5 * u, ct, tenue.motif, false)
    rect(g, -2.9 * l * u, 0, 5.8 * l * u, 9 * u, ct, tenue.motif)          // bras
    g.translate(0, 9 * u); g.rotate(cd)
    rect(g, -2.7 * l * u, 0, 5.4 * l * u, 7 * u, ct, tenue.motif)          // avant-bras
    g.translate(0, 7 * u)
    rect(g, -3.2 * l * u, 0, 6.4 * l * u, 5.4 * u, cg, 'plis')             // gant
    g.fillStyle = rgba('#c89a5a', av ? .7 : .4); g.fillRect(-3.2 * l * u, 0, 6.4 * l * u, 1.3 * u)
    if (av && p.outil) {
      g.rotate(.1)
      /* le manche : la main le tient vers son HAUT, il file vers le bas */
      decoupe(g, () => { g.beginPath(); g.rect(-1.7 * u, -5 * u, 3.4 * u, 32 * u) },
        '#8a5a28', 'plis', { x: -2 * u, y: -6 * u, w: 4 * u, h: 34 * u }, 2, 3)
      g.fillStyle = '#c8a03a'; g.fillRect(-2.2 * u, 21 * u, 4.4 * u, 2.6 * u)      // la virole
      /* LA TÊTE, au bout ÉLOIGNÉ du manche.
         🚨 TROIS FOIS REPRISE (14/08). Ce qui manquait, et que son croquis a tranché d'un trait :
         LA CONCAVITÉ REGARDE LE MANCHE. Les deux becs se recourbent VERS l'utilisateur, jamais
         vers l'extérieur — c'est ce qui distingue une pioche d'une faux, et je l'avais à l'envers
         dans les trois versions précédentes. Le milieu du fer est donc le point le PLUS ÉLOIGNÉ
         de la main (y max), les deux pointes REMONTENT vers elle.
         Le reste tient à l'asymétrie, sans quoi ça reste un croissant :
           · un PIC long et effilé du côté du regard, c'est lui qui mord ;
           · une PANNE courte et large de l'autre ;
           · une DOUILLE d'acier au milieu, le collet où passe le manche. */
      const acier = '#b6bcc4', ombre = '#8d949e'
      // le PIC : il part du collet, s'éloigne et REMONTE vers la main en s'affinant
      decoupe(g, () => { g.beginPath()
        g.moveTo(-2.5 * u, 27 * u); g.quadraticCurveTo(-10 * u, 27.2 * u, -16.5 * u, 22.6 * u)
        g.lineTo(-15.6 * u, 25.2 * u); g.quadraticCurveTo(-9.5 * u, 30 * u, -2.5 * u, 31 * u)
        g.closePath() },
        acier, 'plis', { x: -17 * u, y: 22 * u, w: 16 * u, h: 10 * u }, 2, 3)
      // la PANNE : plus courte, elle remonte de la même façon
      decoupe(g, () => { g.beginPath()
        g.moveTo(2.5 * u, 27.4 * u); g.quadraticCurveTo(8 * u, 27 * u, 11.8 * u, 24.2 * u)
        g.lineTo(12.2 * u, 27.4 * u); g.quadraticCurveTo(8.5 * u, 30 * u, 2.5 * u, 31 * u)
        g.closePath() },
        ombre, 'plis', { x: 2 * u, y: 23 * u, w: 11 * u, h: 9 * u }, 2, 3)
      // la DOUILLE : le collet, plus sombre, qui tient les deux fers sur le manche
      g.fillStyle = '#6f757e'; g.fillRect(-3.4 * u, 25.6 * u, 6.8 * u, 6.4 * u)
      g.fillStyle = rgba('#ffffff', .22); g.fillRect(-3.4 * u, 25.6 * u, 6.8 * u, 1.2 * u)
      /* le reflet court le long du dos du pic */
      g.save(); g.translate(-9.5 * u, 27 * u); g.rotate(-.36)
      g.fillStyle = rgba('#ffffff', .4); g.fillRect(-4.5 * u, 0, 9 * u, 1.2 * u)
      g.restore()
      g.restore()
    }
    g.restore()
  }

  /* ---- PLAN LOIN --------------------------------------------------------- */
  jambe(false, p.cuAR, p.gnAR)
  bras(false, p.epAR, p.cdAR)

  /* ---- LE BASSIN, entre les deux plans ----------------------------------- */
  { const [gx, gy] = E(0, 15)
    const larg = deFace ? 23 : 20
    /* le bassin prend UN TIERS du décalage du buste : le corps se PLIE, il ne se
       casse pas en deux. Sans ça le torse part en avant et les hanches restent. */
    rect(g, (deFace ? -11.5 : -9.5) * u + BU[0] * u / 3 + gx, HANCHE - 2 * u + gy, larg * u, 9 * u, tenue.jambe, tenue.motif) }

  /* ---- LE TORSE : étroit, décalé vers l'avant ---------------------------- */
  g.save(); const [tx, ty] = E(0, -8)
  g.translate(BU[0] * u * .5 + tx, BU[1] * u + ty); g.rotate(BU[2])
  const tG = (deFace ? -12 : -9.5) * u, tL = (deFace ? 24 : 21.5) * u
  { const f = (p.foulard || 0), a = -f * .9 - .25 + Math.sin(tick / 7) * .1 * f
    g.save(); g.translate(tG + 1 * u, -32 * u); g.rotate(a)
    decoupe(g, () => { g.beginPath(); g.moveTo(0, -2 * u)
      g.quadraticCurveTo(-9 * u, 3 * u, -17 * u - f * 5 * u, 1 * u)
      g.quadraticCurveTo(-9 * u, 8 * u, 0, 5 * u); g.closePath() },
      '#c8384a', 'plis', { x: -24 * u, y: -4 * u, w: 26 * u, h: 14 * u }, 2, 3)
    g.restore() }
  if (deFace) rect(g, tG, -33 * u, tL, 14 * u, tenue.torse, tenue.motif)
  else {
    /* trapèze : épaules larges en haut, taille resserrée en bas, et le bord AVANT
       qui descend 1,5 u plus bas que l'arrière. C'est cette silhouette, et non un
       rectangle, qui dit de quel côté le buste est tourné. */
    decoupe(g, () => { g.beginPath()
      g.moveTo(tG - 1 * u, -33 * u); g.lineTo(tG + tL + 1 * u, -33 * u)
      g.lineTo(tG + tL - .5 * u, -17.5 * u); g.lineTo(tG + 2 * u, -19 * u); g.closePath() },
      tenue.torse, tenue.motif, { x: tG - 2 * u, y: -34 * u, w: tL + 4 * u, h: 18 * u }, 3, 4)
  }
  /* l'arête du buste : une bande plus sombre côté ARRIÈRE, qui creuse le volume
     et achève de dire dans quel sens le corps est tourné */
  if (!deFace) { g.fillStyle = rgba(sombre(tenue.torse, .72), .85); g.fillRect(tG, -33 * u, 4 * u, 14 * u) }
  rect(g, tG, -22 * u, tL, 4 * u, '#5a3d1e', 'plis', false)                     // ceinture
  g.fillStyle = tenue.accent; g.fillRect((deFace ? -3 : -1) * u, -22.6 * u, 6 * u, 5 * u)
  g.fillStyle = tenue.accent                                                     // bretelles
  g.fillRect(tG + 2.5 * u, -33 * u, 3 * u, 12 * u); g.fillRect(tG + tL - 5.5 * u, -33 * u, 3 * u, 12 * u)
  g.fillStyle = rgba('#ffffff', .5); g.fillRect(tG + 1.5 * u, -34 * u, tL - 3 * u, 3 * u)  // col
  g.fillStyle = rgba(OMBRE_TON, .16); g.fillRect(tG + tL - 9 * u, -29 * u, 6 * u, 6 * u)   // poche
  g.restore()

  /* ---- LA TÊTE : de trois-quarts, l'œil avant plus grand ----------------- */
  g.save(); const [hx2, hy2] = E(0, -34)
  g.translate(TE[0] * u * .5 + hx2, TE[1] * u + hy2); g.rotate(TE[2])
  const hG = (deFace ? -10 : -7.2) * u, hL = (deFace ? 20 : 16) * u
  rect(g, hG, -44 * u, hL, 11 * u, PEAU, 'plis')
  if (!deFace) {
    /* le nez : une avancée de 2 u côté face. Rien d'autre ne dit aussi vite
       qu'une tête est tournée. */
    rect(g, hG + hL - .5 * u, -39.5 * u, 2.6 * u, 3 * u, PEAU, 'plis', false)
    /* l'oreille, côté arrière */
    g.fillStyle = rgba('#d8a878', .9); g.fillRect(hG + 1 * u, -39 * u, 2.4 * u, 3.4 * u)
    g.fillStyle = rgba(sombre(PEAU, .82), .7); g.fillRect(hG, -44 * u, 3 * u, 11 * u)  // joue à l'ombre
  }
  g.fillStyle = ENCRE
  const oeil = (dx: number, w: number): void => { if (cligne) g.fillRect(dx * u, -39 * u, w * u, 1.2 * u)
                            else g.fillRect(dx * u, -40 * u, w * u, 3 * u) }
  if (deFace) { oeil(-5.5, 3); oeil(2.5, 3) }
  else { oeil(1, 2); oeil(4.8, 2.8) }                // l'œil du fond est plus étroit
  g.fillStyle = rgba('#c07a5a', .55)
  g.fillRect((deFace ? 5 : 5.4) * u, -37 * u, 2.8 * u, 1.8 * u)
  chapeau4(g, deFace ? 0 : 1 * u, -44 * u, u, chapeau || 'casque', tenue)
  if ((chapeau || 'casque') === 'casque' && DETAIL > .25) {
    /* le faisceau : discret. À la taille du jeu il noyait la tête sous une tache
       floue plus large que le personnage. */
    const R = 9 * u
    const gl = g.createRadialGradient(9 * u, -46 * u, 1, 9 * u, -46 * u, R)
    gl.addColorStop(0, `rgba(255,246,200,${.42 * DETAIL})`); gl.addColorStop(1, 'rgba(255,240,160,0)')
    g.fillStyle = gl; g.beginPath(); g.arc(9 * u, -46 * u, R, 0, 7); g.fill()
  }
  g.restore()

  /* ---- PLAN PROCHE ------------------------------------------------------- */
  jambe(true, p.cuAV, p.gnAV)
  bras(true, p.epAV, p.cdAV)
  g.restore()
}

/* Chaque chapeau : UNE découpe posée. Ajouter le treizième ne demande rien d'autre. */
export function chapeau4(g: Ctx, cx: number, tete: number, u: number, kind: string, tenue: Tenue): void {
  const O = (fn: (o: boolean) => void): void => { g.save(); g.translate(3 * u, 4 * u); g.fillStyle = rgba(OMBRE_TON, .3); fn(true); g.restore() }
  const formes: Record<string, (o: boolean) => void> = {
    casque: (o: boolean) => { g.fillStyle = o ? g.fillStyle : (tenue.accent || '#ffcf1f')
      g.fillRect(cx - 13 * u, tete - 5 * u, 26 * u, 6 * u)
      g.beginPath(); g.ellipse(cx, tete, 13 * u, 7 * u, 0, Math.PI, 0); g.fill()
      if (!o) { g.fillStyle = '#fffbe0'; g.beginPath(); g.arc(cx - 9 * u, tete - 3 * u, 3 * u, 0, 7); g.fill() } },
    paille: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#e0bd6a'
      g.beginPath(); g.ellipse(cx, tete - 1 * u, 22 * u, 4.5 * u, 0, 0, 7); g.fill()
      g.beginPath(); g.ellipse(cx, tete - 5 * u, 11 * u, 6 * u, 0, Math.PI, 0); g.fill()
      if (!o) { g.fillStyle = '#a8442e'; g.fillRect(cx - 11 * u, tete - 3.5 * u, 22 * u, 2.6 * u) } },
    bonnet: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#c8302c'
      g.beginPath(); g.ellipse(cx, tete, 13 * u, 10 * u, 0, Math.PI, 0); g.fill()
      g.fillRect(cx - 13 * u, tete - 2 * u, 26 * u, 4 * u)
      if (!o) { g.fillStyle = '#fff0e8'; g.beginPath(); g.arc(cx, tete - 12 * u, 4 * u, 0, 7); g.fill() } },
    cornes: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#5a6068'
      g.beginPath(); g.ellipse(cx, tete, 12 * u, 7 * u, 0, Math.PI, 0); g.fill()
      if (!o) g.fillStyle = '#efe3c8'
      for (const s of [-1, 1]) { g.beginPath(); g.moveTo(cx + s * 10 * u, tete - 3 * u)
        g.quadraticCurveTo(cx + s * 20 * u, tete - 10 * u, cx + s * 16 * u, tete - 1 * u); g.closePath(); g.fill() } },
    hautdeforme: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#1e1a24'
      g.beginPath(); g.ellipse(cx, tete - 1 * u, 17 * u, 3.6 * u, 0, 0, 7); g.fill()
      g.fillRect(cx - 10 * u, tete - 17 * u, 20 * u, 16 * u)
      if (!o) { g.fillStyle = '#8b2b3a'; g.fillRect(cx - 10 * u, tete - 6 * u, 20 * u, 3.4 * u) } },
    aureole: (o: boolean) => { if (o) return
      g.strokeStyle = '#ffd23f'; g.lineWidth = 3 * u
      g.beginPath(); g.ellipse(cx, tete - 9 * u, 11 * u, 3.6 * u, 0, 0, 7); g.stroke() },
    couronne: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#f0c020'
      g.beginPath(); g.moveTo(cx - 12 * u, tete)
      for (let k = 0; k <= 4; k++) { const px = cx - 12 * u + k * 6 * u
        g.lineTo(px, tete - 9 * u); g.lineTo(px + 3 * u, tete - 3 * u) }
      g.lineTo(cx + 12 * u, tete); g.closePath(); g.fill()
      if (!o) { g.fillStyle = '#d8354a'; g.beginPath(); g.arc(cx, tete - 2 * u, 2.4 * u, 0, 7); g.fill() } },
    oreilles: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#3a3038'
      for (const s of [-1, 1]) { g.beginPath(); g.moveTo(cx + s * 4 * u, tete + 1 * u)
        g.lineTo(cx + s * 8 * u, tete - 11 * u); g.lineTo(cx + s * 13 * u, tete - 1 * u); g.closePath(); g.fill() }
      if (!o) { g.fillStyle = '#e8a0b0'
        for (const s of [-1, 1]) { g.beginPath(); g.moveTo(cx + s * 6.5 * u, tete)
          g.lineTo(cx + s * 8.5 * u, tete - 7 * u); g.lineTo(cx + s * 11 * u, tete - .5 * u); g.closePath(); g.fill() } } },
    fleurs: (o: boolean) => { if (o) return
      const cols: string[] = ['#e86a8a', '#f0c93a', '#8fd06a', '#7ab8e8', '#d888e0']
      for (let k = 0; k < 5; k++) { const a = -.2 - k * .68
        g.fillStyle = cols[k]!; g.beginPath(); g.arc(cx + Math.cos(a) * 12 * u, tete - 2 * u + Math.sin(a) * 6 * u, 3.4 * u, 0, 7); g.fill() } },
    tricorne: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#20264a'
      g.beginPath(); g.moveTo(cx - 19 * u, tete + 1 * u); g.quadraticCurveTo(cx, tete - 15 * u, cx + 19 * u, tete + 1 * u)
      g.quadraticCurveTo(cx, tete - 5 * u, cx - 19 * u, tete + 1 * u); g.fill()
      if (!o) { g.fillStyle = '#e0b83a'; g.fillRect(cx - 15 * u, tete - 1 * u, 30 * u, 2.2 * u)
        g.fillStyle = '#c83a3a'; g.beginPath(); g.ellipse(cx + 13 * u, tete - 9 * u, 3 * u, 7 * u, .5, 0, 7); g.fill() } },
    bois: (o: boolean) => { g.strokeStyle = o ? rgba(OMBRE_TON, .3) : '#9a7040'; g.lineWidth = 2.6 * u; g.lineCap = 'round'
      for (const s of [-1, 1]) { g.beginPath(); g.moveTo(cx + s * 6 * u, tete)
        g.lineTo(cx + s * 11 * u, tete - 13 * u); g.stroke()
        g.beginPath(); g.moveTo(cx + s * 9 * u, tete - 7 * u); g.lineTo(cx + s * 16 * u, tete - 9 * u); g.stroke()
        g.beginPath(); g.moveTo(cx + s * 10.5 * u, tete - 11 * u); g.lineTo(cx + s * 17 * u, tete - 16 * u); g.stroke() } },
    sorcier: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#5a2f9a'
      g.beginPath(); g.ellipse(cx, tete, 17 * u, 3.6 * u, 0, 0, 7); g.fill()
      g.beginPath(); g.moveTo(cx - 12 * u, tete); g.quadraticCurveTo(cx + 2 * u, tete - 14 * u, cx + 9 * u, tete - 24 * u)
      g.quadraticCurveTo(cx + 6 * u, tete - 6 * u, cx + 12 * u, tete); g.closePath(); g.fill()
      if (!o) { g.fillStyle = '#ffd23f'; g.beginPath(); g.arc(cx + 9 * u, tete - 24 * u, 3 * u, 0, 7); g.fill() } },
    /* ⚠️ AJOUTÉ LE 30/07, APRÈS DEUX JOURS D'ABSENCE. La refonte papier du 28/07 a redessiné
       onze chapeaux sur douze et oublié celui-ci — le plus RARE du jeu (méga, 1 %). Comme le
       Casque ailé est marqué `remplace: true` dans shared/accessoires.ts, `chapeauDuJeu` retirait
       la calotte du Baroudeur puis `chapeau4` sortait sans rien peindre (`if (!forme) return`) :
       le joueur qui décrochait le loot le plus rare se retrouvait CRÂNE NU, donc plus mal loti
       qu'en n'équipant rien. Le silence de `if (!forme) return` est ce qui l'a caché deux jours.
       Toute addition à shared/accessoires.ts doit désormais arriver AVEC sa forme ici. */
    ailes: (o: boolean) => { g.fillStyle = o ? g.fillStyle : '#e8b830'
      /* le bandeau d'or, qui prend la place de la calotte */
      g.beginPath(); g.ellipse(cx, tete + 1 * u, 12.5 * u, 8 * u, 0, Math.PI, 0); g.fill()
      g.fillRect(cx - 12.5 * u, tete - 3 * u, 25 * u, 4.5 * u)
      /* les deux ailes, en trois plumes chacune : trois découpes se lisent encore à 44 px,
         un éventail de six devient une tache */
      if (!o) g.fillStyle = '#dfe6ee'
      for (const s of [-1, 1]) for (let k = 0; k < 3; k++) {
        /* le trait ALLER passe loin en dehors, le trait RETOUR revient près de la
           base : c'est cet écart qui donne l'épaisseur. Deux courbes symétriques
           donnaient une plume en fil de fer, invisible à la taille du jeu. */
        g.beginPath()
        g.moveTo(cx + s * (9 - k) * u, tete - (1 + k * 2.5) * u)
        g.quadraticCurveTo(cx + s * (22 + k * 3) * u, tete - (5 + k * 4) * u,
                           cx + s * (15 + k * 4) * u, tete - (11 + k * 5.5) * u)
        g.quadraticCurveTo(cx + s * (11 + k * 1.5) * u, tete - (1 + k * 2) * u,
                           cx + s * (9 - k) * u, tete - (1 + k * 2.5) * u)
        g.fill()
      }
      if (o) return
      g.fillStyle = '#aab6c4'                              // l'ombre portée d'une plume sur l'autre
      for (const s of [-1, 1]) {
        g.beginPath(); g.moveTo(cx + s * 9 * u, tete - 1 * u)
        g.quadraticCurveTo(cx + s * 17 * u, tete - 5 * u, cx + s * 13 * u, tete - 8 * u)
        g.quadraticCurveTo(cx + s * 13 * u, tete - 3.5 * u, cx + s * 9 * u, tete - 1 * u); g.fill()
      }
      g.fillStyle = '#fff0b0'                              // le jonc lumineux du bandeau
      g.fillRect(cx - 12.5 * u, tete - 2.6 * u, 25 * u, 1.6 * u) },

    /* ── LES QUATRE DE LA VAGUE DU 30/07 ────────────────────────────────────
       Validés sur /pages/cosmetiques.html avant d'entrer ici. */

    /* CASQUETTE À HÉLICE — courant. La silhouette la plus immédiate qu'on puisse
       poser sur une tête : calotte basse, visière courte, hélice à quatre pales.
       Les pales sont peintes en croix, jamais alignées avec le regard, sinon de
       profil il n'en reste qu'un trait. */
    helice: (o: boolean) => {
      g.fillStyle = o ? g.fillStyle : '#3a72c8'
      g.beginPath(); g.ellipse(cx, tete, 13 * u, 9 * u, 0, Math.PI, 0); g.fill()   // calotte
      g.beginPath(); g.ellipse(cx - 4 * u, tete + 1 * u, 17 * u, 3 * u, 0, 0, 7); g.fill()  // visière
      if (o) return
      g.fillStyle = '#d8342e'                                                      // quartier rouge
      g.beginPath(); g.moveTo(cx, tete - 9 * u)
      g.quadraticCurveTo(cx + 7 * u, tete - 6 * u, cx + 9 * u, tete)
      g.lineTo(cx + 3 * u, tete); g.closePath(); g.fill()
      g.fillStyle = '#c8a030'                                                      // le pivot
      g.fillRect(cx - 1.4 * u, tete - 13 * u, 2.8 * u, 5 * u)
      for (let k = 0; k < 4; k++) {                                                // les quatre pales
        const a = k * Math.PI / 2 + .35
        g.fillStyle = k % 2 ? '#f0e8d4' : '#e0d0b0'
        g.save(); g.translate(cx, tete - 13 * u); g.rotate(a)
        g.beginPath(); g.ellipse(7 * u, 0, 7 * u, 2 * u, 0, 0, 7); g.fill()
        g.restore()
      }
      g.fillStyle = '#8a6a18'
      g.beginPath(); g.arc(cx, tete - 13 * u, 1.8 * u, 0, 7); g.fill() },

    /* TOQUE DE CUISINIER — courant. Tout est dans la BOSSE : trois arcs qui
       débordent du bandeau. Une toque droite ressemblerait à un haut-de-forme
       blanc, donc à un chapeau qu'on a déjà. */
    toque: (o: boolean) => {
      g.fillStyle = o ? g.fillStyle : '#f4efe2'
      for (const [dx, dy, r] of [[-7, 15, 7.5], [0, 18, 8.5], [7, 15, 7.5]] as const) {
        g.beginPath(); g.arc(cx + dx * u, tete - dy * u, r * u, 0, 7); g.fill()
      }
      g.fillRect(cx - 11 * u, tete - 16 * u, 22 * u, 16 * u)
      g.beginPath(); g.ellipse(cx, tete, 11 * u, 4 * u, 0, 0, 7); g.fill()          // bandeau
      if (o) return
      g.fillStyle = '#ddd3bd'                                                       // l'ourlet
      g.fillRect(cx - 11 * u, tete - 4.5 * u, 22 * u, 4.5 * u)
      g.strokeStyle = rgba(OMBRE_TON, .22); g.lineWidth = 1.3 * u                   // deux plis
      for (const dx of [-4, 4]) {
        g.beginPath(); g.moveTo(cx + dx * u, tete - 5 * u); g.lineTo(cx + dx * u, tete - 14 * u); g.stroke()
      } },

    /* HEAUME DE CHEVALIER — rare. Le seul chapeau du jeu qui FERME le visage ;
       tous les autres se posent sur le crâne et laissent les yeux à l'air.
       ⚠️ Les repères viennent du dessin de la tête ci-dessus : elle occupe 11 u
       sous `tete` et les yeux sont peints à `tete + 4 u`. La fente se place
       exactement là. Ailleurs, le heaume n'est qu'un bol posé sur le crâne. */
    heaume: (o: boolean) => {
      g.fillStyle = o ? g.fillStyle : '#8f9aa8'
      g.beginPath(); g.ellipse(cx, tete + 2 * u, 11.5 * u, 12 * u, 0, Math.PI, 0); g.fill()  // le dôme
      g.fillRect(cx - 11.5 * u, tete + 1 * u, 23 * u, 8 * u)                        // la ventaille
      g.beginPath(); g.moveTo(cx - 11.5 * u, tete + 8.5 * u)                        // le menton, rentrant
      g.lineTo(cx + 11.5 * u, tete + 8.5 * u); g.lineTo(cx + 8 * u, tete + 12 * u)
      g.lineTo(cx - 8 * u, tete + 12 * u); g.closePath(); g.fill()
      g.beginPath(); g.ellipse(cx, tete - 10 * u, 3.6 * u, 2.8 * u, 0, 0, 7); g.fill()  // le cimier
      if (o) return
      g.fillStyle = '#15121c'                                                       // la fente, à hauteur d'yeux
      g.fillRect(cx - 9.5 * u, tete + 2.8 * u, 19 * u, 3.2 * u)
      g.fillStyle = '#6f7a88'                                                       // les trous d'aération
      for (let k = 0; k < 4; k++) g.fillRect(cx - 5.6 * u + k * 3.4 * u, tete + 7 * u, 1.5 * u, 1.5 * u)
      g.fillStyle = '#c9d2dc'                                                       // l'arête lumineuse
      g.fillRect(cx - 1.2 * u, tete - 10 * u, 2.4 * u, 12 * u)
      g.strokeStyle = rgba(ENCRE, .32); g.lineWidth = 1.1 * u                       // rivets
      for (const dx of [-9, 9]) {
        g.beginPath(); g.arc(cx + dx * u, tete + 1.2 * u, 1.3 * u, 0, 7); g.stroke()
      }
      g.fillStyle = '#c83a3a'                                                       // le plumet
      g.beginPath(); g.moveTo(cx, tete - 11 * u)
      g.quadraticCurveTo(cx + 15 * u, tete - 25 * u, cx + 7 * u, tete - 3 * u)
      g.quadraticCurveTo(cx + 9 * u, tete - 16 * u, cx, tete - 11 * u); g.fill() },

    /* NUAGE D'ORAGE — méga, et le seul qui NE REMPLACE PAS le casque : il flotte
       au-dessus, comme l'auréole. C'est ce qui en fait un méga lisible de loin,
       une silhouette qui déborde du personnage au lieu de l'habiller.

       ⚠️ L'ÉCLAIR EST BLANC CERNÉ D'ENCRE, ET C'EST UNE LEÇON. Il était d'abord
       peint dans `tenue.accent` — jolie idée : le méga s'accorde à ce que porte
       le joueur. Sauf que l'accent de la tenue d'origine EST le jaune du casque
       de mineur, et que cet accessoire garde le casque sous lui : l'éclair
       sortait jaune sur jaune, invisible, exactement là où il devait frapper.
       Un cosmétique qui se pose PAR-DESSUS un autre porte son propre contraste,
       il ne l'emprunte jamais à ce qu'il recouvre. */
    orage: (o: boolean) => {
      g.fillStyle = o ? g.fillStyle : '#63718a'
      for (const [dx, dy, r] of [[-9, 12, 6], [-2, 15, 7.5], [6, 13, 6.5], [11, 10, 4.5]] as const) {
        g.beginPath(); g.arc(cx + dx * u, tete - dy * u, r * u, 0, 7); g.fill()
      }
      g.fillRect(cx - 13 * u, tete - 12 * u, 25 * u, 5 * u)
      if (o) return
      g.fillStyle = '#96a4bc'                                                       // le dessus éclairé
      for (const [dx, dy, r] of [[-9, 14, 4], [-2, 17.5, 5], [6, 15, 4.2]] as const) {
        g.beginPath(); g.arc(cx + dx * u, tete - dy * u, r * u, 0, 7); g.fill()
      }
      g.fillStyle = '#3f4a5e'                                                       // le ventre du nuage
      g.fillRect(cx - 12 * u, tete - 8 * u, 23 * u, 1.6 * u)
      /* l'éclair, décroché sur la GAUCHE pour tomber à côté du casque et non
         dessus, et cerné d'encre pour rester lisible quoi qu'il y ait dessous */
      const eclair = (): void => {
        g.beginPath()
        g.moveTo(cx - 11 * u, tete - 7 * u); g.lineTo(cx - 17 * u, tete + 1 * u)
        g.lineTo(cx - 13 * u, tete + .5 * u); g.lineTo(cx - 16 * u, tete + 8 * u)
        g.lineTo(cx - 7 * u, tete - 1 * u); g.lineTo(cx - 11.5 * u, tete - .5 * u)
        g.closePath()
      }
      g.fillStyle = rgba('#ffd23f', .35); g.lineWidth = 4.5 * u                     // le halo
      g.strokeStyle = rgba('#ffd23f', .35); eclair(); g.stroke()
      g.strokeStyle = ENCRE; g.lineWidth = 2 * u; eclair(); g.stroke()               // le cerne
      g.fillStyle = '#fff6c8'; eclair(); g.fill()                                    // le cœur blanc
      g.fillStyle = rgba('#8fd0f0', .85)                                             // deux gouttes
      for (const [dx, dy] of [[7, 3], [12, -1]] as const) {
        g.beginPath(); g.ellipse(cx + dx * u, tete - dy * u, 1.6 * u, 2.6 * u, 0, 0, 7); g.fill()
      } },

    /* ── LA VAGUE DU 05/08 (planche /pages/cosmetiques2.html) ───────────────
       Trois formes proposées, dessinées ICI et nulle part ailleurs : la planche
       les importe. Tant que Claude-Alain n'a pas tranché, AUCUNE n'est dans le
       catalogue de tirage — une forme sans loot ne coûte rien et ne se voit pas. */

    /* CHAPEAU CHAMPIGNON — courant. Tout est dans le DÉBORD : une calotte qui
       dépasse largement du crâne fait le champignon, une calotte ajustée ferait
       un béret à pois. Le dessous crème rappelle les lamelles. */
    champignon: (o: boolean) => {
      g.fillStyle = o ? g.fillStyle : '#d8342e'
      g.beginPath(); g.ellipse(cx, tete - 1 * u, 18 * u, 12.5 * u, 0, Math.PI, 0); g.fill()
      if (o) return
      g.fillStyle = '#f2e4cc'                                   // le dessous, en lamelles
      g.beginPath(); g.ellipse(cx, tete - 1 * u, 18 * u, 2.8 * u, 0, 0, 7); g.fill()
      g.fillStyle = '#fff8ee'                                   // les pois, jamais alignés
      for (const [dx, dy, r] of [[-10, 6, 2.7], [-2, 9.6, 2.1], [7, 5.5, 2.4], [13, 2.2, 1.5]] as const) {
        g.beginPath(); g.arc(cx + dx * u, tete - dy * u, r * u, 0, 7); g.fill()
      } },

    /* LAMPE DE MINEUR — rare. Le casque de base a déjà sa petite lampe : celui-ci
       inverse le contraste. Coque anthracite, GROSSE lampe, et un vrai faisceau
       qui part vers la GAUCHE — l'avant du héros, là où le jeu met le bec du
       canari et le museau du loup. Le faisceau est translucide : il se pose sur
       le décor sans le recouvrir. */
    lampe: (o: boolean) => {
      g.fillStyle = o ? g.fillStyle : '#3a3f46'
      g.fillRect(cx - 13 * u, tete - 4 * u, 26 * u, 5 * u)                              // le rebord
      g.beginPath(); g.ellipse(cx, tete - 1 * u, 13 * u, 9 * u, 0, Math.PI, 0); g.fill()
      if (o) return
      /* le faisceau : deux cônes imbriqués nés du verre, PENCHÉS VERS LE BAS —
         une frontale éclaire le chemin, pas le plafond. Un seul cône à .16
         était invisible sur la planche ; deux couches font le cœur dense. */
      g.fillStyle = rgba('#ffec8c', .24)
      g.beginPath(); g.moveTo(cx - 11 * u, tete - 6 * u)
      g.lineTo(cx - 31 * u, tete - 2 * u); g.lineTo(cx - 31 * u, tete + 15 * u)
      g.lineTo(cx - 11 * u, tete - 2 * u); g.closePath(); g.fill()
      g.fillStyle = rgba('#ffec8c', .26)
      g.beginPath(); g.moveTo(cx - 11 * u, tete - 5.2 * u)
      g.lineTo(cx - 27 * u, tete - .5 * u); g.lineTo(cx - 27 * u, tete + 10 * u)
      g.lineTo(cx - 11 * u, tete - 2.8 * u); g.closePath(); g.fill()
      g.fillStyle = '#565d66'                                   // l'arête de la coque
      g.fillRect(cx - 1.2 * u, tete - 9.5 * u, 2.4 * u, 6 * u)
      g.fillStyle = rgba('#ffec8c', .22)                        // le halo, discret
      g.beginPath(); g.arc(cx - 11 * u, tete - 4 * u, 4.4 * u, 0, 7); g.fill()
      g.strokeStyle = '#22262c'; g.lineWidth = 1.5 * u          // le boîtier
      g.beginPath(); g.arc(cx - 11 * u, tete - 4 * u, 3 * u, 0, 7); g.stroke()
      g.fillStyle = '#fff3b0'                                   // le verre allumé
      g.beginPath(); g.arc(cx - 11 * u, tete - 4 * u, 2.7 * u, 0, 7); g.fill() },

    /* NID D'OISILLON — épique, et il GARDE le casque : le nid s'est posé dessus,
       comme s'il avait poussé là pendant la partie. L'oisillon regarde à gauche,
       bec ouvert — c'est LUI le cosmétique, le nid n'est que son socle. */
    nid: (o: boolean) => {
      g.fillStyle = o ? g.fillStyle : '#8a6234'
      g.beginPath(); g.ellipse(cx, tete + 3.5 * u, 11 * u, 5 * u, 0, 0, 7); g.fill()    // le bol, ASSIS sur le casque
      if (o) return
      g.strokeStyle = '#a87c46'; g.lineWidth = 1.2 * u          // brindilles qui dépassent
      for (const [x1, y1, x2, y2] of [[-11, .5, -16, 2.5], [11, 1.5, 15, 4.5], [-9, 3.5, -14, 6.5], [10, -.5, 16, -.5]] as const) {
        g.beginPath(); g.moveTo(cx + x1 * u, tete - y1 * u); g.lineTo(cx + x2 * u, tete - y2 * u); g.stroke()
      }
      g.fillStyle = '#6d4c26'                                   // le creux
      g.beginPath(); g.ellipse(cx, tete + 1 * u, 8 * u, 2.6 * u, 0, 0, 7); g.fill()
      g.fillStyle = '#ffd23f'                                   // l'oisillon
      g.beginPath(); g.arc(cx - 2 * u, tete - 3 * u, 4.2 * u, 0, 7); g.fill()
      g.strokeStyle = '#f0a500'; g.lineWidth = 1.1 * u          // trois plumes sur le crâne
      for (const dx of [-3.4, -1.8, -.2]) {
        g.beginPath(); g.moveTo(cx + dx * u, tete - 6.7 * u); g.lineTo(cx + (dx - .7) * u, tete - 9.1 * u); g.stroke()
      }
      g.fillStyle = '#ef7a10'                                   // le bec, grand ouvert
      g.beginPath(); g.moveTo(cx - 5.8 * u, tete - 3.7 * u); g.lineTo(cx - 9.4 * u, tete - 5.5 * u)
      g.lineTo(cx - 5.8 * u, tete - 4.7 * u); g.closePath(); g.fill()
      g.beginPath(); g.moveTo(cx - 5.8 * u, tete - 2.9 * u); g.lineTo(cx - 9.2 * u, tete - 1.7 * u)
      g.lineTo(cx - 5.8 * u, tete - 1.9 * u); g.closePath(); g.fill()
      g.fillStyle = '#1e1a24'                                   // l'œil
      g.beginPath(); g.arc(cx - 3.2 * u, tete - 4.3 * u, 1 * u, 0, 7); g.fill() },
  }
  const forme = formes[kind]
  if (!forme) return
  O(() => forme(true)); forme(false)
}
